import { tz } from '@date-fns/tz';
import { format } from 'date-fns';
import { styled } from 'styled-components';

import type {
  ApiISODateString,
  ApiISODateTimeNoTzString,
  ApiISODateTimeString,
} from 'api/types/ApiTypedDate';
import { Box } from 'components/Box/Box';
import { DefaultButton as TooltipButton } from 'components/LegacyTooltip/LegacyTooltip.styled';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { colors } from 'theme/theme';
import { DATE_FNS_LOCALES, getDate, humanDate, shorterTime } from 'utils/date';
import { getUserTimezone } from 'utils/timezones';

const StyledTooltipButton = styled(TooltipButton)`
  color: ${colors.lightContentGrey};

  &:hover {
    color: ${colors.brandBlue};
  }
`;

type TimeTooltipProps = {
  startDateTime: string;
  endDateTime?: string;
  timezone: string | null | undefined;
};

export function TimeTooltip({
  startDateTime,
  endDateTime,
  timezone,
}: TimeTooltipProps) {
  const startTime = shorterTime(startDateTime, timezone, !endDateTime);
  const endTime = endDateTime ? shorterTime(endDateTime, timezone, true) : '';
  return (
    <Box ml="8px" mr="4px">
      <Tooltip
        variant="filled"
        content={
          <>
            {startTime}
            {endTime ? ' - ' : ''}
            {endTime}
          </>
        }
      >
        <StyledTooltipButton />
      </Tooltip>
    </Box>
  );
}

type SingleDayDateTimeProps = {
  startDate: ApiISODateString | ApiISODateTimeNoTzString | ApiISODateTimeString;
  endDate: ApiISODateString | ApiISODateTimeNoTzString | ApiISODateTimeString;
  showStartTime?: boolean;
  showEndTime?: boolean;
  timezone?: string | null;
  'data-qa-id'?: string;
};

export function SingleDayDateTime({
  startDate,
  endDate,
  showStartTime,
  showEndTime,
  timezone,
  'data-qa-id': dataQaId,
}: SingleDayDateTimeProps) {
  const startDateTime = shorterTime(startDate, timezone, !endDate);
  const endDateTime = endDate ? shorterTime(endDate, timezone, true) : '';
  const userTimezone = getUserTimezone();
  return (
    <div
      data-qa-id={dataQaId}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <div>{humanDate(startDate, timezone)}</div>
      {showStartTime && showEndTime && (
        <Box display="flex" alignItems="center">
          <>
            {startDateTime}
            {showEndTime ? ' - ' : ''}
            {endDateTime}
          </>
          {userTimezone !== timezone && (
            <TimeTooltip
              startDateTime={startDate}
              endDateTime={endDate}
              timezone={userTimezone}
            />
          )}
        </Box>
      )}
    </div>
  );
}

type DateTimeProps = {
  dateTime: ApiISODateTimeNoTzString | ApiISODateTimeString;
  timezone: string | null | undefined;
  'data-qa-id'?: string;
};

export function FormattedDateTime({
  dateTime,
  timezone,
  'data-qa-id': dataQaId,
}: DateTimeProps) {
  const userTimezone = getUserTimezone();

  return (
    <div data-qa-id={dataQaId}>
      <div style={{ display: 'inline-block' }}>
        {timezone
          ? format(dateTime, 'MMMM d, yyyy, h:mm a zzz', {
              in: tz(timezone),
              locale: DATE_FNS_LOCALES[CURRENT_LOCALE()],
            })
          : format(getDate(dateTime), 'MMMM d, yyyy, h:mm a', {
              locale: DATE_FNS_LOCALES[CURRENT_LOCALE()],
            })}
      </div>

      {userTimezone !== timezone && (
        <div style={{ display: 'inline-block', marginLeft: '4px' }}>
          <TimeTooltip startDateTime={dateTime} timezone={userTimezone} />
        </div>
      )}
    </div>
  );
}

type DateProps = {
  date: ApiISODateString;
  'data-qa-id'?: string;
};

export function FormattedDate({ date, 'data-qa-id': dataQaId }: DateProps) {
  return (
    <div data-qa-id={dataQaId}>
      <div style={{ display: 'inline-block' }}>
        {format(getDate(date), 'MMMM d, yyyy', {
          locale: DATE_FNS_LOCALES[CURRENT_LOCALE()],
        })}
      </div>
    </div>
  );
}
